import axiosInstance from './axiosInstance';

const API_URL = '/api/v1/support/tickets';

// ── Types ────────────────────────────────────────────────────────────

export interface TicketAttachment {
  id: string;
  message_id: string;
  file_name: string;
  content_type: string;
  file_size: number;
  created_at: string;
}

export interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string | null;
  sender_name: string | null;
  sender_role: string | null;
  content: string;
  is_internal_note: boolean;
  is_system_message: boolean;
  attachments: TicketAttachment[];
  created_at: string;
}

export interface Ticket {
  id: string;
  user_id: string;
  creator_name: string | null;
  creator_email: string | null;
  assigned_to: string | null;
  assignee_name: string | null;
  status: string;
  priority: string;
  category: string | null;
  preview: string | null;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
}

export interface TicketDetail {
  id: string;
  user_id: string;
  creator_name: string | null;
  creator_email: string | null;
  assigned_to: string | null;
  assignee_name: string | null;
  status: string;
  priority: string;
  category: string | null;
  preview?: string | null;
  messages: TicketMessage[];
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
}

export interface TicketListResponse {
  tickets: Ticket[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface SupportNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface SupportNotificationListResponse {
  notifications: SupportNotification[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// ── Service ──────────────────────────────────────────────────────────

export const ticketService = {
  createTicket: async (data: {
    content: string;
    category?: string;
    priority?: string;
  }): Promise<TicketDetail> => {
    const response = await axiosInstance.post(`${API_URL}/`, data);
    return response.data;
  },

  listTickets: async (params: {
    page?: number;
    per_page?: number;
    status?: string;
    priority?: string;
    category?: string;
  } = {}): Promise<TicketListResponse> => {
    const response = await axiosInstance.get(`${API_URL}/`, { params });
    return response.data;
  },

  getTicketDetail: async (ticketId: string): Promise<TicketDetail> => {
    const response = await axiosInstance.get(`${API_URL}/${ticketId}`);
    return response.data;
  },

  assignTicket: async (
    ticketId: string,
    assignedTo: string
  ): Promise<TicketDetail> => {
    const response = await axiosInstance.put(`${API_URL}/${ticketId}/assign`, {
      assigned_to: assignedTo,
    });
    return response.data;
  },

  updatePriority: async (
    ticketId: string,
    priority: string
  ): Promise<TicketDetail> => {
    const response = await axiosInstance.put(`${API_URL}/${ticketId}/priority`, {
      priority,
    });
    return response.data;
  },

  updateStatus: async (
    ticketId: string,
    status: string
  ): Promise<TicketDetail> => {
    const response = await axiosInstance.put(`${API_URL}/${ticketId}/status`, {
      status,
    });
    return response.data;
  },

  addMessage: async (
    ticketId: string,
    data: { content: string; is_internal_note?: boolean }
  ): Promise<TicketMessage> => {
    const response = await axiosInstance.post(
      `${API_URL}/${ticketId}/messages`,
      data
    );
    return response.data;
  },

  uploadAttachments: async (
    ticketId: string,
    messageId: string,
    files: File[]
  ): Promise<{ attachments: TicketAttachment[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await axiosInstance.post(
      `${API_URL}/${ticketId}/messages/${messageId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  downloadAttachment: async (attachmentId: string): Promise<Blob> => {
    const response = await axiosInstance.get(
      `${API_URL}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  getSupportStaff: async (): Promise<SupportStaffUser[]> => {
    const response = await axiosInstance.get(`${API_URL}/staff`);
    return response.data;
  },

  getUnreadNotificationCount: async (): Promise<number> => {
    const response = await axiosInstance.get(`${API_URL}/notifications/unread-count`);
    return response.data?.unread_count ?? 0;
  },

  listNotifications: async (params: {
    page?: number;
    per_page?: number;
    unread_only?: boolean;
  } = {}): Promise<SupportNotificationListResponse> => {
    const response = await axiosInstance.get(`${API_URL}/notifications`, { params });
    return response.data;
  },

  markNotificationRead: async (notificationId: string): Promise<void> => {
    await axiosInstance.put(`${API_URL}/notifications/${notificationId}/read`);
  },

  markAllNotificationsRead: async (): Promise<void> => {
    await axiosInstance.put(`${API_URL}/notifications/read-all`);
  },
};

export interface SupportStaffUser {
  id: string;
  email: string;
  display_name: string;
  system_role: string;
}
